import * as Sentry from '@sentry/react';
import { trackDesignSystemAction, trackLearningAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { SHOWCASE_DESIGN_SYSTEMS } from 'components/Modal/DsShowcaseModal';
import { CreateSceneChatResponse, CreateSceneResponse } from 'dragonmouse-shared/api/forge';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
	selectDesignSystems,
	selectOrganizations,
	selectUser,
	selectUserSettings,
} from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayDsShowcaseModal } from 'redux/mode/util/modals';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import CreatorInput from './CreatorInput';
import * as S from './styles';

type CreatorMode = 'project' | 'designSystemPlan';

interface CreatorProps {
	mode?: CreatorMode;
}

// One creator page with two modes. The mode lives in the URL (`/` vs
// `/create-ds`), so it survives refresh and both destinations deep-link.
const Creator = ({ mode = 'project' }: CreatorProps) => {
	const navigate = useNavigate();
	const theme = useTheme();
	// The creator has its own loader. Like the projects page, it does some
	// things without optimistic updates. When it does this, it shows a semi-transparent loading
	// which is different from the main App's loading indicator (which is opaque).
	// [perhaps we should generalize the main app's loader - ss]
	//
	// We use the existence of a loading message to gate the display of the loader
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const isDsMode = mode === 'designSystemPlan';

	const {
		isMobile,
		organizations,
		defaultOrganizationId,
		defaultDesignSystemId,
		designSystems,
		user,
	} = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			organizations: selectOrganizations(r.db),
			defaultOrganizationId: selectUserSettings(r.db).defaultOrganizationId,
			defaultDesignSystemId: selectUserSettings(r.db).defaultDesignSystemId,
			designSystems: selectDesignSystems(r.db),
			user: selectUser(r.db),
		}),
		shallowEqual
	);

	// Design systems are desktop-only — the editor itself refuses to render on
	// mobile — so the creator must not hand out one that can never be opened.
	// Guarding the route rather than each entry point covers every way in: the
	// scene chat's picker, the showcase footer, a bookmark, a shared link.
	// `deviceConfig` resolves just after mount, so this runs on that transition.
	const isDsModeOnMobile = isDsMode && isMobile;
	useEffect(() => {
		if (!isDsModeOnMobile) return;
		toast.info('Design systems are only available on desktop.');
		navigate(routes.home(), { replace: true });
	}, [isDsModeOnMobile, navigate]);

	// The DS creator is a distinct funnel step; keep its page-view metric alive.
	// Mobile visits bounce straight back out, so they are not funnel entries.
	useEffect(() => {
		if (isDsMode && !isMobile) trackDesignSystemAction('view_create_page');
	}, [isDsMode, isMobile]);

	const [selectedDesignSystemId, setSelectedDesignSystemId] = useState<string | null>(
		defaultDesignSystemId ?? null
	);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(
		defaultOrganizationId ?? null
	);
	const prevDefaultOrgRef = useRef<string | null | undefined>(undefined);

	// Sync local state when default org changes (profile load or user changed it in Settings)
	useEffect(() => {
		if (prevDefaultOrgRef.current !== defaultOrganizationId) {
			prevDefaultOrgRef.current = defaultOrganizationId;
			setSelectedOrganizationId(defaultOrganizationId ?? null);
		}
	}, [defaultOrganizationId]);

	// Validate that default org still exists; clear if deleted
	useEffect(() => {
		if (!defaultOrganizationId || !user || user.id === 'guest') return;
		const orgExists = organizations.some((org) => org.id === defaultOrganizationId);
		if (!orgExists) {
			setSelectedOrganizationId(null);
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						settings: { ...user.metadata?.settings, defaultOrganizationId: null },
					},
				},
			});
		}
	}, [organizations, defaultOrganizationId, user]);

	// Sync local state when user profile loads with a default DS
	useEffect(() => {
		if (defaultDesignSystemId && !selectedDesignSystemId) {
			setSelectedDesignSystemId(defaultDesignSystemId);
		}
	}, [defaultDesignSystemId]);

	// Validate that default DS still exists; clear if deleted
	useEffect(() => {
		if (defaultDesignSystemId && designSystems.length > 0) {
			const dsExists = designSystems.some((ds) => ds.id === defaultDesignSystemId);
			if (!dsExists && user && user.id !== 'guest') {
				forge.updateUser({
					update: {
						id: user.id,
						metadata: {
							settings: { ...user.metadata?.settings, defaultDesignSystemId: null },
						},
					},
				});
			}
		}
	}, [designSystems, defaultDesignSystemId, user]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onNewProject = async (
		prompt: string,
		attachments: Attachment[],
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => {
		try {
			setLoadingMessage('creating project...');
			trackLearningAction('onboarding_prompt_input', 'complete');
			const effectiveDsId = designSystemId ?? selectedDesignSystemId;
			if (effectiveDsId) trackDesignSystemAction('create_scene_with_ds', { dsId: effectiveDsId });
			const params = {
				prompt,
				attachments,
				...(effectiveDsId && { designSystemId: effectiveDsId }),
				...(effectiveDsId && designSystemVersionNumber && { designSystemVersionNumber }),
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			};
			const res: CreateSceneChatResponse = await forge.createSceneChatFromPrompt.withToast()(
				params
			);
			setLoadingMessage('');
			navigate(routes.scene(res.scene.id, res.scene.title));
		} catch (e) {
			Sentry.captureException(e);
			setLoadingMessage('');
		}
	};

	const onDesignSystemSelect = (designSystemId: string | null) => {
		setSelectedDesignSystemId(designSystemId);
	};

	const onOwnershipSelect = (organizationId: string | null) => {
		setSelectedOrganizationId(organizationId);
	};

	const onCreateEmptyProject = async (
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => {
		try {
			setLoadingMessage('creating project...');
			const effectiveDsId = designSystemId ?? selectedDesignSystemId;
			if (effectiveDsId) trackDesignSystemAction('create_scene_with_ds', { dsId: effectiveDsId });
			const res: CreateSceneResponse = await forge.createScene.withToast()({
				title: 'My Project',
				...(effectiveDsId && { designSystemId: effectiveDsId }),
				...(effectiveDsId && designSystemVersionNumber && { designSystemVersionNumber }),
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			});
			setLoadingMessage('');
			navigate(routes.scene(res.sceneId, 'My Project'));
		} catch (e) {
			Sentry.captureException(e);
			setLoadingMessage('');
		}
	};

	const onNewDesignSystem = async (prompt: string, attachments: Attachment[]) => {
		try {
			setLoadingMessage('designing your system...');
			trackDesignSystemAction('create_ds_from_prompt');
			const res = await forge.createDesignSystemFromPrompt.withToast()({
				prompt,
				...(attachments.length > 0 && { attachments }),
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			});
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
		} catch (e) {
			Sentry.captureException(e);
			setLoadingMessage('');
		}
	};

	// The blank path mirrors a blank project: no naming step, straight into a
	// default-named system
	const onCreateEmptyDesignSystem = async () => {
		try {
			setLoadingMessage('creating design system...');
			trackDesignSystemAction('created_blank_ds');
			const res = await forge.createDesignSystem.withToast()({
				title: DEFAULT_DS_TITLE,
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			});
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
		} catch (e) {
			Sentry.captureException(e);
			setLoadingMessage('');
		}
	};

	const onBrowseStarterKits = () => {
		trackDesignSystemAction('click_browse_gallery');
		displayDsShowcaseModal({
			mode: 'view',
			onSelect: (item) => navigate(item.dsUrl),
		});
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	// Nouns name what gets created; the headline keeps the verb. These labels
	// match the sidebar create menu word for word. The control navigates between
	// routes rather than selecting an in-page panel, so it carries navigation
	// semantics (aria-current), not tablist roles.
	const renderSelector = () => (
		<S.Segmented>
			<S.SegmentedTab
				type="button"
				aria-current={!isDsMode ? 'page' : undefined}
				$isActive={!isDsMode}
				onClick={() => isDsMode && navigate(routes.home(), { replace: true })}
			>
				Project
			</S.SegmentedTab>
			<S.SegmentedTab
				type="button"
				aria-current={isDsMode ? 'page' : undefined}
				$isActive={isDsMode}
				onClick={() => !isDsMode && navigate(routes.createDesignSystem(), { replace: true })}
				data-testid="creator-mode-design-system"
			>
				Design System
			</S.SegmentedTab>
		</S.Segmented>
	);

	// Seeds end mid-sentence at "link:" with the cursor parked after it, so what
	// to paste is self-evident and nothing needs deleting
	const dsPromptSeeds = [
		{ label: 'From a GitHub repo', seed: 'Import my design system from this GitHub repo link: ' },
		{ label: 'From a Figma file', seed: 'Create a design system from this Figma link: ' },
		{ label: 'From a website', seed: 'Create a design system based on this website link: ' },
		{ label: 'Random example', useExample: true },
	];

	const projectPromptSeeds = [{ label: 'Random example', useExample: true }];

	const renderComposer = () =>
		isDsMode ? (
			<CreatorInput
				onSubmit={onNewDesignSystem}
				onCreateBlank={onCreateEmptyDesignSystem}
				disabled={!!loadingMessage}
				mode="designSystemPlan"
				organizationId={selectedOrganizationId}
				organizations={organizations}
				onOwnershipSelect={onOwnershipSelect}
				promptSeeds={dsPromptSeeds}
			/>
		) : (
			<CreatorInput
				onSubmit={onNewProject}
				disabled={!!loadingMessage}
				mode="project"
				onDesignSystemSelect={onDesignSystemSelect}
				initialDesignSystemId={defaultDesignSystemId}
				onCreateBlank={onCreateEmptyProject}
				organizationId={selectedOrganizationId}
				organizations={organizations}
				onOwnershipSelect={onOwnershipSelect}
				promptSeeds={projectPromptSeeds}
			/>
		);

	const renderStarterKitEntry = () => {
		if (!isDsMode) return null;
		return (
			<S.Teaser type="button" onClick={onBrowseStarterKits} data-testid="ds-browse-gallery-button">
				<S.TeaserThumbs>
					{SHOWCASE_DESIGN_SYSTEMS.slice(0, 3).map((item) => (
						<S.ThumbFrame key={item.designSystemId}>
							<S.ThumbImg src={item.hiResImageUrl || item.imageUrl} alt="" />
						</S.ThumbFrame>
					))}
				</S.TeaserThumbs>
				<S.TeaserCopy>
					<S.TeaserTitle>Start from a starter kit</S.TeaserTitle>
					<S.TeaserDescription>
						{SHOWCASE_DESIGN_SYSTEMS.length} ready-made systems you can make your own
					</S.TeaserDescription>
				</S.TeaserCopy>
				<S.TeaserArrow>
					<Icon type={IconType.LINE_ARROW_RIGHT} width={16} height={16} color={theme.gray[400]} />
				</S.TeaserArrow>
			</S.Teaser>
		);
	};

	// Hold the frame between device detection and the redirect above, so the
	// design-system creator never flashes on a device that cannot use one
	if (isDsModeOnMobile) return null;

	return (
		<S.Base>
			{isMobile && (
				<S.SidebarToggle>
					<SidebarToggle />
				</S.SidebarToggle>
			)}
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<S.Page>
				<S.Column>
					{!isMobile && <S.SelectorAbove>{renderSelector()}</S.SelectorAbove>}
					<S.TitleSlot>
						<S.Title data-testid="home-page-title">
							{isDsMode ? (
								<>What kind of design system do you&nbsp;need?</>
							) : (
								<>What would you like to design&nbsp;today?</>
							)}
						</S.Title>
					</S.TitleSlot>
					{renderComposer()}
					{renderStarterKitEntry()}
				</S.Column>
			</S.Page>
		</S.Base>
	);
};

export default Creator;
